import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '../routing';
import { formatCurrencyWhole } from '../utils/api';
import { categoryLineColor } from '../utils/classificationColors';
import SearchableSelect from './SearchableSelect';
import ExpandCaret from './ExpandCaret';
import Icon from './Icon';
import { useIsMobile } from '../utils/useIsMobile';
import { useSheetDrag } from '../utils/useSheetDrag';

// Wiring spend into קטגוריות, by supplier or by item-type classification.
//
// THE JOB THIS SHAPE SOLVES. Production restaurants carry 42-138 suppliers and
// most of that list does not matter: on בסטה בר, 37 suppliers cover 80% of the
// unmapped money. So the work is 20-50 decisions, not 138 — but only if the
// screen says so. Hence rows ranked by the money STILL OUTSTANDING, a queue
// that shrinks as you work, and a headline that names the remaining gap.
//
// SUPPLIERS COME FIRST — and, since they can always finish the job alone, they
// are now the ONLY axis on the page. Every line of spend has a supplier, so the
// supplier list always reaches 100% of the gap; item types reach only what has
// been tagged, which on 5 of 16 production restaurants is nothing at all.
//
// So classifications moved behind ONE button, into a dialog. As peer tabs they
// claimed half the screen's navigation for a lens that is often worth 0%, and
// they made the screen ask "which way do I work?" before it asked "which
// supplier?".
//
// The button is deliberately BARE — just שיוך מתקדם. It used to carry a row
// count and a `N% מהחוסר` reach (what share of the remaining gap this lens
// could still close), inherited from the tab labels. The owner asked for both
// off: on a button that is already the secondary path, two numbers nobody had
// been told how to read cost more attention than they returned. Anything that
// argues for opening the dialog now belongs INSIDE it.

const SEGMENTS = 20;

// Desktop modal / mobile bottom sheet, chosen in CSS off one DOM rather than a
// JS viewport branch, so a resize cannot strand it in the wrong form.
//
// The body is a scroll container, which is the one thing this screen has had a
// bug about before: SearchableSelect's menu is absolutely positioned inside its
// row, so a scroll container can clip it. Two things keep that from biting —
// classifications run 3-19 rows, so this usually does not scroll at all, and the
// menu opens DOWNWARD, which extends the scrollable area rather than hiding
// behind its edge.
function AdvancedDialog({ open, onClose, title, children }) {
  // ≤720px this is a bottom sheet with a handle: swipe down closes it. Phone
  // only — on desktop .bmap-adv is centred with a transform the drag would clobber.
  const isMobile = useIsMobile(720);
  const sheetRef = useRef(null);
  const backdropRef = useRef(null);
  useSheetDrag(sheetRef, onClose, { enabled: open && isMobile, backdropRef });

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <div ref={backdropRef} className="bmap-adv-backdrop" onClick={onClose} aria-hidden />
      <div ref={sheetRef} className="bmap-adv" role="dialog" aria-modal="true" aria-label={title}>
        <div className="bmap-adv-handle" aria-hidden />
        <div className="bmap-adv-header">
          <span className="bmap-adv-title">{title}</span>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="סגור">
            <Icon name="close" size={16} strokeWidth={1.8} />
          </button>
        </div>
        <div className="bmap-adv-body">{children}</div>
      </div>
    </>,
    document.body,
  );
}

function Rows({
  kind, items, lines, onMap, emptyText, queueOnly, setQueueOnly, savingId, untagged,
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  // 'all' | 'none' | String(line.id). 'none' is the one people actually came
  // for — "what have I not decided yet" — but it is NOT the same question as
  // the טרם שויך queue: a row can carry a rule and still have money outstanding,
  // and a rule-less row can already be budgeted through its products. So this
  // filters on the RULE, and the queue chips filter on the MONEY.
  const [lineFilter, setLineFilter] = useState('all');

  // Picking one specific line answers a different question than 'all'/'none':
  // "what is tied to X", full stop — not "what is tied to X AND still has
  // money outstanding". Composed with the default טרם שויך queue, that second
  // reading silently swallowed rows that were fully mapped to X and returned
  // a bare אין תוצאות for a category that plainly has rows — the queue chip
  // answering a question nobody asked it. 'none' keeps composing with the
  // queue (see comment above): "no rule AND still outstanding" is a real
  // question, unlike "tied to X AND still outstanding".
  const onSpecificLine = lineFilter !== 'all' && lineFilter !== 'none';
  const effectiveQueueOnly = queueOnly && !onSpecificLine;

  // queueOnly itself is lifted to the parent and shared with the OTHER Rows
  // instance (suppliers vs. classifications), so it must stay the single
  // source of truth for the chips' own state — but the chips must be judged
  // against effectiveQueueOnly, not queueOnly, or they visually claim a mode
  // ("טרם שויך" pressed) that contradicts what the table is actually showing.
  // Disabled rather than merely relabelled while onSpecificLine: the toggle
  // has no effect on THIS view once a specific line is picked, and letting it
  // still fire would silently change the OTHER Rows instance's queueOnly the
  // moment this one looks inert.

  const lineOptions = useMemo(
    () => lines.map((l, i) => ({ value: String(l.id), label: l.name, color: categoryLineColor(l, i) })),
    [lines],
  );

  // The clear entry is a real option, not just the placeholder: without it a
  // rule could be set but never removed — the server accepts null and the
  // handler maps '' to null, but nothing in the list produced ''. A mapping
  // screen you cannot un-map is a trap, since the first wrong guess is
  // permanent.
  //
  // It appears ONLY on a row that has something to clear. SearchableSelect
  // displays the option matching the current value, and an unassigned row's
  // value is '' — so an always-present clear entry became the label of every
  // undecided row, which then read "הסר שיוך" on a row with no שיוך. The
  // placeholder has to win there, and it can only win if nothing matches.
  const optionsFor = (item) => (
    item.category_line_id != null
      ? [{ value: '', label: 'הסר שיוך' }, ...lineOptions]
      : lineOptions
  );

  const shown = useMemo(() => {
    let base = effectiveQueueOnly ? items.filter(i => i.unmapped > 0) : items;
    if (lineFilter === 'none') base = base.filter(i => i.category_line_id == null);
    else if (onSpecificLine) base = base.filter(i => String(i.category_line_id) === lineFilter);
    return q ? base.filter(i => (i.name || '').toLowerCase().includes(q)) : base;
  }, [items, effectiveQueueOnly, q, lineFilter, onSpecificLine]);

  // Rows the search MATCHES but the other two filters are hiding.
  //
  // The trap this closes: assign a supplier while the dropdown sits on
  // ללא קטגוריה (or the queue chip is on). The row now carries a rule, so it
  // correctly leaves the view — the filter doing its declared job — but then
  // searching its NAME also came back "אין תוצאות", because search ran on top
  // of the same filters. To the person who just assigned it, that sequence
  // reads as data loss: the supplier they touched is gone and the screen
  // insists it does not exist. Search stays scoped to the current view (a
  // filter you cannot search within is its own trap), but when it comes back
  // short it now says WHY, and hands over the one-click way out.
  const hiddenMatches = useMemo(() => {
    if (!q) return 0;
    return items.filter(i => (i.name || '').toLowerCase().includes(q)).length - shown.length;
  }, [items, q, shown]);

  if (items.length === 0) {
    return <p style={{ color: '#94a3b8', fontSize: 13, margin: '8px 0' }}>{emptyText}</p>;
  }

  const moneyHeader = effectiveQueueOnly ? 'לא משויך · 90 יום' : 'רכש · 90 יום';

  return (
    <>
      {/* One line, two groups. Search and the קטגוריה filter sit TOGETHER because
          they are the same act — narrowing to the rows you mean, by name or by
          rule. The queue chips are the other question (how much money is left)
          and hold the far end of the bar. */}
      <div className="bmap-filterbar">
        <div className="bmap-find">
          <input
            className="form-control bmap-search" placeholder="חיפוש…"
            aria-label={`חיפוש ב${kind === 'classification' ? 'סיווגים' : 'ספקים'}`}
            value={query} onChange={e => setQuery(e.target.value)}
          />
          <select
            className="form-control bmap-linefilter"
            aria-label="סינון לפי קטגוריה"
            value={lineFilter} onChange={e => setLineFilter(e.target.value)}
          >
            <option value="all">כל הקטגוריות</option>
            <option value="none">ללא קטגוריה</option>
            {lines.map(l => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
          </select>
        </div>
        <div className="bmap-chips" role="group" aria-label="הצגה">
          {[
            { k: true, label: 'טרם שויך', n: items.filter(i => i.unmapped > 0).length },
            { k: false, label: 'הכול', n: items.length },
          ].map(f => (
            <button
              key={String(f.k)} type="button"
              className={`btn btn-sm ${effectiveQueueOnly === f.k ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setQueueOnly(f.k)}
              aria-pressed={effectiveQueueOnly === f.k}
              disabled={onSpecificLine}
              title={onSpecificLine ? 'לא רלוונטי בעת סינון לפי קטגוריה ספציפית — הרשימה מציגה הכול' : undefined}
            >
              {f.label} <bdi>({f.n})</bdi>
            </button>
          ))}
        </div>
      </div>

      {/* No inner max-height. A scroll region inside a scrolling page is
          hostile on touch, and it was also clipping the select's menu on the
          lower rows — the menu is positioned inside this element. */}
      <div className="table-wrapper">
        <table className="bmap-table">
          <thead>
            <tr>
              <th>{kind === 'classification' ? 'סיווג פריט' : 'ספק'}</th>
              <th style={{ width: 150 }}>{moneyHeader}</th>
              <th style={{ width: 210 }}>קטגוריה</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(item => {
              const partial = item.unmapped > 0 && item.unmapped < item.spend;
              return (
                <tr key={item.id} className={savingId === item.id ? 'bmap-saving' : undefined}>
                  <td data-th="שם">{item.name}</td>
                  <td data-th={moneyHeader} className="bmap-money">
                    {effectiveQueueOnly ? formatCurrencyWhole(item.unmapped) : formatCurrencyWhole(item.spend)}
                    {/* Only in the queue: says why this row's figure is smaller
                        than the supplier's actual invoices. Without it the
                        number looks simply wrong to anyone who knows the
                        supplier. */}
                    {effectiveQueueOnly && partial && (
                      <span className="bmap-sub">מתוך {formatCurrencyWhole(item.spend)} · חלק כבר בתקציב</span>
                    )}
                  </td>
                  <td data-th="קטגוריה">
                    <SearchableSelect
                      value={item.category_line_id != null ? String(item.category_line_id) : ''}
                      options={optionsFor(item)}
                      placeholder="בחרו קטגוריה"
                      disabled={savingId === item.id}
                      ariaLabel={`שיוך ${item.name} לקטגוריה`}
                      onChange={v => onMap(kind, item.id, v === '' ? null : Number(v))}
                    />
                  </td>
                </tr>
              );
            })}

            {/* Spend on products with no item type at all. It has no classification,
                so there is nothing here to attach a rule to — it is context and
                a way out, never a row with a dropdown. Without it, a restaurant
                with no tagging saw a list of classifications worth nothing and no
                explanation of why this tab could not help. */}
            {kind === 'classification' && untagged?.unmapped > 0 && !q
              && !onSpecificLine && (
              <tr className="bmap-untagged">
                <td data-th="שם">לא מסווג</td>
                <td data-th={moneyHeader} className="bmap-money">{formatCurrencyWhole(untagged.unmapped)}</td>
                <td data-th="קטגוריה">
                  <Link to="/item-classifications">שייכו מוצרים לסיווגים</Link>
                </td>
              </tr>
            )}

            {/* The search found rows the filters are hiding — see hiddenMatches
                above. Rendered even when SOME rows are shown, because "found 1
                of the 2 you meant" hides the other one just as silently. */}
            {hiddenMatches > 0 && (
              <tr>
                <td colSpan={3} style={{ color: '#94a3b8' }}>
                  <span>
                    {hiddenMatches === 1
                      ? 'תוצאה אחת מוסתרת על ידי הסינון'
                      : `${hiddenMatches} תוצאות מוסתרות על ידי הסינון`}
                  </span>
                  {' · '}
                  <button
                    type="button"
                    onClick={() => { setQueueOnly(false); setLineFilter('all'); }}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      font: 'inherit', fontWeight: 600, color: '#0f3460',
                      textDecoration: 'underline', textUnderlineOffset: 2,
                    }}
                  >הצגת הכול</button>
                </td>
              </tr>
            )}

            {shown.length === 0 && hiddenMatches === 0 && (
              <tr>
                <td colSpan={3} style={{ color: '#94a3b8' }}>
                  {/* "הכול משויך" is a claim about the DATA, so it may only be
                      made when nothing is narrowing the view — otherwise a
                      filter that matched nothing congratulates you on work you
                      have not done. */}
                  {q || lineFilter !== 'all' ? 'אין תוצאות' : 'הכול משויך'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function BudgetMappingPanel({
  lines = [], classifications = [], suppliers = [], mappings, onMap, savingId = null,
  open = true, onToggle,
}) {
  // Suppliers are the page; item types are a dialog — see the note at the top.
  const [advanced, setAdvanced] = useState(false);
  const [queueOnly, setQueueOnly] = useState(true);

  const total = mappings?.totals?.total || 0;
  const gap = mappings?.totals?.unmapped || 0;
  const gapPct = total > 0 ? Math.round((gap / total) * 100) : 0;
  const days = mappings?.window?.days;
  const done = total > 0 && gap <= 0;

  const filled = total > 0 ? Math.round(((total - gap) / total) * SEGMENTS) : 0;

  return (
    <div className={`card category-lines-card bmap-card${open ? '' : ' is-collapsed'}`}>
      <button
        type="button" className="category-lines-toggle"
        onClick={onToggle} aria-expanded={open} aria-controls="bmap-body"
      >
        <span className="category-lines-title">שיוך רכש לקטגוריות</span>
        {/* A short one-liner, same job as every sibling's summary: glanceable
            content beside the caret instead of a dead gap (an empty span here
            used to read as a cut-off/broken row next to the other three
            cards, which all show something in this spot). Deliberately
            terser than the cov-headline below — that one states the full
            amount + % + day window; this is just the percentage, so the two
            don't read as a duplicate. */}
        <span className="category-lines-summary">{done ? 'הושלם' : `${gapPct}% לא משויך`}</span>
        <ExpandCaret open={open} size={14} />
      </button>

      {/* The gap, stated as money first. It used to trail a coverage
          percentage as a grey clause after a `·`, in agorot, which is the
          typography of a footnote — and it was losing the page to a target bar
          reading 96.1% that describes only the mapped quarter.

          Shown only when open: the toggle's own summary span already carries
          the percentage collapsed, and repeating the full sentence there (on
          top of the summary AND the bar) was one number stated three times. */}
      {open && (done ? (
        <p className="cov-headline">כל הרכש משויך לקטגוריות</p>
      ) : (
        <p className="cov-headline">
          לא משויך {formatCurrencyWhole(gap)}
          <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 13 }}>
            {' · '}<bdi>{gapPct}%</bdi> מהרכש{days ? <> · <bdi>{days}</bdi> יום</> : null}
          </span>
        </p>
      ))}

      {/* Deliberately NOT gated on `open` (owner): this card is the one place
          on /budget where the coverage bar itself is worth keeping visible
          even collapsed — it's the whole point of the section, at a glance,
          without a click. The other three cards fully condense; this one
          doesn't, on purpose, so it looks different from its siblings by
          design rather than by omission. aria-hidden because the toggle's
          summary states the same percentage in words for screen readers. */}
      <div className="cov-bar" aria-hidden="true">
        {Array.from({ length: SEGMENTS }, (_, i) => (
          <span key={i} className={`cov-seg${i < filled ? ' is-on' : ''}`} />
        ))}
      </div>

      {open && (
      <div id="bmap-body">
      {/* One axis on the page, one behind a button — see the note at the top of
          this file. The heading names what the list below IS, so the button
          beside it reads as the other way to do this rather than as a filter on
          the list. */}
      <div className="bmap-axisbar">
        <span className="bmap-axis-title">
          שיוך ע״פ ספק <bdi>({suppliers.length})</bdi>
        </span>
        <button
          type="button" className="bmap-advanced-btn"
          onClick={() => setAdvanced(true)}
          aria-haspopup="dialog" aria-expanded={advanced}
        >
          <Icon name="tag" size={14} />
          שיוך מתקדם
        </button>
      </div>

      <Rows
        kind="supplier" items={suppliers} lines={lines} onMap={onMap}
        queueOnly={queueOnly} setQueueOnly={setQueueOnly} savingId={savingId}
        emptyText="אין ספקים."
      />
      </div>
      )}

      <AdvancedDialog
        open={advanced} onClose={() => setAdvanced(false)}
        title="שיוך מתקדם לפי סיווגים"
      >
        {/* Said here rather than on the button: it is the reason this axis is
            the advanced one, and it only matters once you have opened it. */}
        <p className="bmap-adv-note">
          כלל לפי סיווג גובר על כלל לפי ספק, ומגיע רק לרכש של מוצרים שכבר שויכו לסיווג.
        </p>
        <Rows
          kind="classification" items={classifications} lines={lines} onMap={onMap}
          queueOnly={queueOnly} setQueueOnly={setQueueOnly} savingId={savingId}
          untagged={mappings?.untagged}
          emptyText="אין סיווג פריטים — שייכו לפי ספק."
        />
      </AdvancedDialog>
    </div>
  );
}
